import React, { ReactElement, useRef, useEffect, useState, CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { prefixClass } from 'lib/utils';
import classNames from 'classnames';
import { useTooltip } from '../hooks/useTooltip';
import { getFloatingPositionClasses } from '../hooks/useFloatingPosition';
import { useSmartPosition } from '../hooks/useSmartPosition';
import type { TooltipConfig } from '../hooks/useTooltip';
import './Tooltip.scss';

const prefix = prefixClass('ksq2-tooltip');

export interface TooltipProps extends TooltipConfig {
  /** The element that triggers the tooltip */
  children: ReactElement;
  /** Additional className for tooltip container */
  className?: string;
}

/**
 * Tooltip wrapper component for complex cases
 * For simple cases, use the tooltip prop on components like IconButton
 */
export const Tooltip: React.FC<TooltipProps> = ({
  children,
  content,
  position = 'top',
  alignment = 'center',
  showDelay = 300,
  hideDelay = 100,
  disabled = false,
  className,
}) => {
  const { triggerProps, tooltipState, tooltipId } = useTooltip({
    content,
    position,
    alignment,
    showDelay,
    hideDelay,
    disabled,
  });

  // Wrap children in a span to ensure ref always attaches to a real DOM element
  // This fixes issues with functional components that don't use forwardRef
  const childProps = (children as any).props || {};
  const trigger = (
    <span
      ref={triggerProps.ref as React.RefObject<HTMLSpanElement>}
      style={{ display: 'inline-flex' }}
      onMouseEnter={(e: React.MouseEvent) => {
        triggerProps.onMouseEnter();
        if (childProps.onMouseEnter) {
          childProps.onMouseEnter(e);
        }
      }}
      onMouseLeave={(e: React.MouseEvent) => {
        triggerProps.onMouseLeave();
        if (childProps.onMouseLeave) {
          childProps.onMouseLeave(e);
        }
      }}
      onFocus={(e: React.FocusEvent) => {
        triggerProps.onFocus();
        if (childProps.onFocus) {
          childProps.onFocus(e);
        }
      }}
      onBlur={(e: React.FocusEvent) => {
        triggerProps.onBlur();
        if (childProps.onBlur) {
          childProps.onBlur(e);
        }
      }}
      onClick={(e: React.MouseEvent) => {
        triggerProps.onClick();
        // Don't call childProps.onClick - the child's onClick fires through normal event bubbling
        // Calling it here would cause double-firing for clickable children like buttons
      }}
    >
      {children}
    </span>
  );

  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipSize, setTooltipSize] = useState({ width: 200, height: 100 });

  // Measure tooltip size when it becomes visible
  useEffect(() => {
    if (tooltipState.isVisible && tooltipRef.current) {
      const rect = tooltipRef.current.getBoundingClientRect();
      setTooltipSize({ width: rect.width, height: rect.height });
    }
  }, [tooltipState.isVisible, content]);

  // Use smart positioning to handle viewport boundaries
  const smartPosition = useSmartPosition({
    position,
    alignment,
    triggerRect: tooltipState.triggerRect,
    contentWidth: tooltipSize.width,
    contentHeight: tooltipSize.height,
    gap: 8,
    enableFlip: true,
  });

  const renderTooltip = () => {
    if (!tooltipState.isVisible || !tooltipState.triggerRect) return null;

    const { triggerRect } = tooltipState;
    const finalPosition = smartPosition.position;
    const finalAlignment = smartPosition.alignment;
    const positionClasses = getFloatingPositionClasses(prefix(), finalPosition, finalAlignment);

    // Calculate position based on trigger rect
    const style: CSSProperties = {
      position: 'fixed',
      zIndex: 2000,
    };

    // Position calculations using smart position
    switch (finalPosition) {
      case 'top':
        style.bottom = `${window.innerHeight - triggerRect.top + 8}px`;
        break;
      case 'bottom':
        style.top = `${triggerRect.bottom + 8}px`;
        break;
      case 'left':
        style.right = `${window.innerWidth - triggerRect.left + 8}px`;
        style.top = `${triggerRect.top}px`;
        break;
      case 'right':
        style.left = `${triggerRect.right + 8}px`;
        style.top = `${triggerRect.top}px`;
        break;
    }

    // Alignment calculations using smart alignment
    // Calculate positions directly instead of using transform (more reliable)
    const triggerCenterX = triggerRect.left + triggerRect.width / 2;
    const triggerCenterY = triggerRect.top + triggerRect.height / 2;

    if (finalPosition === 'top' || finalPosition === 'bottom') {
      switch (finalAlignment) {
        case 'start':
          style.left = `${triggerRect.left}px`;
          break;
        case 'center':
          // Center tooltip on trigger by calculating actual left position
          style.left = `${triggerCenterX - tooltipSize.width / 2}px`;
          break;
        case 'end':
          style.right = `${window.innerWidth - triggerRect.right}px`;
          break;
      }
    } else {
      switch (finalAlignment) {
        case 'start':
          style.top = `${triggerRect.top}px`;
          break;
        case 'center':
          // Center tooltip on trigger by calculating actual top position
          style.top = `${triggerCenterY - tooltipSize.height / 2}px`;
          break;
        case 'end':
          style.bottom = `${window.innerHeight - triggerRect.bottom}px`;
          break;
      }
    }

    return createPortal(
      <div
        ref={tooltipRef}
        id={tooltipId}
        role="tooltip"
        className={classNames(prefix(), ...positionClasses, className)}
        style={style}
      >
        <div className={prefix('content')}>{content}</div>
        <div className={prefix('arrow')} />
      </div>,
      document.body
    );
  };

  return (
    <>
      {trigger}
      {renderTooltip()}
    </>
  );
};
